import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import toast from 'react-hot-toast';
import { analytic } from "./utils/Analytics";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
import markdown from 'react-syntax-highlighter/dist/cjs/languages/prism/markdown';
import { oneDark, dracula, vscDarkPlus, oneLight } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import DOMPurify from 'dompurify';

SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('markdown', markdown);

const Report = ({ primary, showReport, setShowReport, selectedMessage, fadeEffect, messages, botId }) => {

    const apiURL = process.env.REACT_APP_API_URL;
    const apiKey = process.env.REACT_APP_BOT_API_KEY;

    const { t } = useTranslation();

    const [selectedOption, setSelectedOption] = useState('Hallucination / false information');
    const [otherText, setOtherText] = useState('');
    const [feedbackText, setFeedbackText] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [reporting, setReporting] = useState(false);

    const language = localStorage.getItem('language') || 'en';
    const syntaxTheme = oneDark;

    const sanitizeContent = (content) => {
        return content
            ?.replace(/</g, '&lt;')
            ?.replace(/>/g, '&gt;');
    };

    const MarkdownComponents = {
        code({ node, inline, className, children, ...props }) {
            const hasLang = /language-(\w+)/.exec(className || '');

            return hasLang ? (
                <SyntaxHighlighter
                    style={syntaxTheme}
                    language={hasLang[1]}
                    PreTag="div"
                    className="codeStyle"
                    showLineNumbers={true}
                    useInlineStyles={true}
                    children={children}
                />
            ) : (
                <code className={className} {...props} children={children} />
            )
        },
        a({ node, ...props }) {
            return <a target="_blank" {...props} />;
        },
        table({ node, ...props }) {
            return (
                <div className='table-responsive'>
                    <table className="table table-bordered" {...props} />
                </div>
            );
        },
        img({ node, ...props }) {
            return (
                <div className="img-container">
                    <img {...props} />
                </div>
            );
        }
    }

    const handleReport = async (reason) => {
        const messageToReport = messages.findIndex((msg) => msg.content === selectedMessage.content);

        const chat_id = localStorage.getItem(`chatId_${botId}`);

        let requestData = {
            "message_index": messageToReport,
            "is_flagged": `1`,
            "reason": reason,
            "comments": feedbackText
        }

        if (chat_id) {
            requestData.chat_id = chat_id
        }

        setReporting(true);

        await axios({
            url: `${apiURL}/flag_message`,
            method: 'POST',
            data: requestData,
            headers: {
                'Api-token': apiKey
            }
        })
            .then((response) => {
                if (response.data.status === 'success') {
                    messages[messageToReport]['is_flagged'] = 1;
                    messages[messageToReport]['reason'] = reason;

                    setSubmitted(true);
                }

                analytic("report_message", { reason: reason, message: selectedMessage.content });
            })
            .catch((err) => {
                console.log(err);
                toast.error(t('Something broke! Please try later.'), {
                    position: 'top-right',
                    style: {
                        backgroundColor: 'white',
                        color: 'red',
                        fontSize: '13px',
                        padding: '5px 7px',
                        fontWeight: '500'
                    }
                })
            })

        setReporting(false);
    }


    const handleRadioChange = (e) => {
        setSelectedOption(e.target.value);
    }

    const handleTextChange = (e) => {
        setOtherText(e.target.value);
    }

    const handleFeedbackChange = (e) => {
        setFeedbackText(e.target.value);
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        handleReport(selectedOption !== 'Other' ? selectedOption : otherText);
    }

    const handleBack = () => {
        setShowReport(false);
        setSubmitted(false);

        setSelectedOption('Hateful or abusive content');
        setOtherText('');
        setFeedbackText('');
    }

    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && showReport) {
                setShowReport(false);
                setSubmitted(false);
            }
        };

        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showReport]);

    return (
        <>
            <div className={`bg-[rgba(0,0,0,0.7)] fixed z-10 flex justify-end flex-col h-full w-full ${showReport ? 'show-report' : 'hide-report'}`} onClick={handleBack} />
            <div className={`menu-ct dark:!bg-[#232326] bg-white transition ease-in-out rounded-t-xl duration-500 z-30 shadow-md w-full fixed left-[0px] bottom-0 ${showReport ? 'show-report' : 'hide-report'}`}>
                <div className="cursor-pointer z-10 absolute top-1 right-2 dark:!text-white text-black" onClick={handleBack}>
                    <svg width="20px" height="20px" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z"  />
                    </svg>
                </div>
                {!submitted ? (
                    <div className={`flex justify-between items-start flex-col px-2 md:!px-8 py-3 md:!py-6 ${fadeEffect}`}>
                        <div className="w-full">
                            <div className="dark:!bg-[#09090c] bg-[#f5f5f5] p-[15px] mt-2 md:!mt-0 rounded-xl mb-3 w-fit">
                                <div className="max-3-lines dark:!text-white text-black">
                                    <ReactMarkdown
                                        components={MarkdownComponents}
                                        className={`markdown-text text-[12px] leading-[18px] text-${language == 'ar' ? 'right' : 'left'}`}
                                        remarkPlugins={[remarkGfm]}
                                        skipHtml={true}
                                    >
                                        {sanitizeContent(selectedMessage?.content)}
                                    </ReactMarkdown>
                                </div>
                            </div>
                            <form>
                                <div className="w-full md:w-1/2 mb-3">
                                    <h4 className="dark:!text-white text-[#333333] text-[14px] font-semibold leading-[22px] mb-2">{t('Why do you want to report this message?')}</h4>
                                    <div className="flex items-center justify-start gap-2">
                                        <div className="radio my-[4px] w-full md:w-1/3">
                                            <label className="flex items-center text-[14px] leading-[16px]">
                                                <input
                                                    type="radio"
                                                    value="Hallucination / false information"
                                                    checked={selectedOption === "Hallucination / false information"}
                                                    onChange={handleRadioChange}
                                                    className={`my-2 w-5 h-4`}
                                                    style={{ accentColor: primary }}
                                                />
                                                <span className="ml-2 text-[13px] dark:!text-white text-[#333333]">{t('Hallucination / false information')}</span>
                                            </label>
                                        </div>
                                        <div className="radio my-[4px] w-full md:w-1/3">
                                            <label className="flex items-center text-[14px] leading-[16px]">
                                                <input
                                                    type="radio"
                                                    value="Irrelevant / not in line with the question"
                                                    checked={selectedOption === "Irrelevant / not in line with the question"}
                                                    onChange={handleRadioChange}
                                                    className={`my-2 w-5 h-4`}
                                                    style={{ accentColor: primary }}
                                                />
                                                <span className="ml-2 text-[13px] dark:!text-white text-[#333333]">{t('Irrelevant / not in line with the question')}</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-start gap-2">
                                        <div className="radio my-[4px] w-full md:w-1/3">
                                            <label className="flex items-center text-[14px] leading-[16px]">
                                                <input
                                                    type="radio"
                                                    value="Didn't use / misused data that was provided"
                                                    checked={selectedOption === "Didn't use / misused data that was provided"}
                                                    onChange={handleRadioChange}
                                                    className={`my-2 w-5 h-4`}
                                                    style={{ accentColor: primary }}
                                                />
                                                <span className="ml-2 text-[13px] dark:!text-white text-[#333333]">{t("Didn't use / misused data that was provided")}</span>
                                            </label>
                                        </div>
                                        <div className="radio my-[4px] w-full md:w-1/3">
                                            <label className="flex items-center text-[14px] leading-[16px]">
                                                <input
                                                    type="radio"
                                                    value="Harmful / hateful content"
                                                    checked={selectedOption === "Harmful / hateful content"}
                                                    onChange={handleRadioChange}
                                                    className={`my-2 w-5 h-4`}
                                                    style={{ accentColor: primary }}
                                                />
                                                <span className="ml-2 text-[13px] dark:!text-white text-[#333333]">{t("Harmful / hateful content")}</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-start gap-2">
                                        <div className="radio my-[4px] w-full md:w-1/3">
                                            <label className="flex items-center text-[14px] leading-[16px]">
                                                <input
                                                    type="radio"
                                                    value="Other"
                                                    checked={selectedOption === "Other"}
                                                    onChange={handleRadioChange}
                                                    className={`my-2 w-5 h-4`}
                                                    style={{ accentColor: primary }}
                                                />
                                                <span className="ml-2 text-[13px] dark:!text-white text-[#333333]">{t('Other')}</span>
                                            </label>
                                        </div>
                                    </div>
                                    {selectedOption == 'Other' && <textarea value={otherText} onChange={handleTextChange} rows={5} placeholder={t('Not appropriate for children')} className="resize-none w-full md:w-[50%] my-2 p-2 text-[14px] leading-[14px] focus:outline-none border dark:!border-[#09090c] outline-none dark:!bg-[#09090c] bg-white text-black dark:!text-white rounded-md mb-0" />}
                                </div>
                                <div className="mb-3">
                                    <h4 className="dark:!text-white text-[#333333] text-[14px] font-semibold leading-[22px] mb-2">{t('How can the response be improved? (Optional)')}</h4>
                                    <textarea value={feedbackText} onChange={handleFeedbackChange} rows={2} placeholder={t('Your feedback...')} className="resize-none w-full mt-2 p-2 text-[14px] leading-[14px] focus:outline-none border dark:!border-[#09090c] dark:!bg-[#09090c] outline-none bg-white text-black dark:!text-white rounded-md" />
                                </div>
                            </form>
                        </div>
                        <div className="w-full flex items-center justify-end gap-3">
                            <button className="block text-center dark:!bg-[#232326] bg-white border border-gray-700 dark:!border-[#232326] dark:!text-white text-gray-700 py-1.5 md:py-3 px-3 md:px-5 text-[14px] font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleBack} disabled={reporting}>
                                {t('Cancel')}
                            </button>
                            <button className="block text-center text-white border dark:!border-[#232326] py-1.5 md:py-3 px-3 md:px-5 text-[14px] font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed" type="submit" onClick={handleSubmit} disabled={reporting} style={{ backgroundColor: primary, borderColor: primary }}>
                                {!reporting ? t('Submit') : t('Submitting...')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`flex items-center justify-center flex-col h-[50vh] fadeIn`}>
                        <img src="/assets/images/submitted-img.png" width={80} height={80} alt="Submitted Icon" />
                        <p className="text-[20px] leading-[32px] text-center dark:text-white text-[#333] font-bold mt-[20px]">{t('Your report has been submitted')}.</p>
                        <p className="text-[16px] leading-[28px] text-center dark:text-white text-[#333] font-semibold px-[15px]">{t('Thanks for your feedback, our team will look into it!')}</p>
                        <button className="w-[30%] px-5 py-3 text-white mt-[30px] border-none rounded-md font-semibold" onClick={handleBack} style={{ backgroundColor: primary }}>{t('OK')}</button>
                    </div>
                )}
            </div>
        </>
    )
}

export default Report;